/**
 * @fileoverview Advanced data grid with filtering, resizing, and export
 * @module ctl-data-grid
 * @version 1.0.0
 *
 * @description
 * An enterprise-grade data grid that extends the base table with advanced features:
 * column filtering (text / number / date), column resizing via drag handles,
 * column reordering via drag-and-drop, server-side pagination, row expansion,
 * fixed header on scroll, and CSV export.
 *
 * Configure columns and rows via JavaScript properties.
 *
 * @example
 * <ctl-data-grid id="grid" page-size="25" resizable filterable exportable></ctl-data-grid>
 * <script type="module">
 *   const grid = document.querySelector('#grid');
 *   grid.columns = [
 *     { id: 'name',   label: 'Name',   sortable: true, filterable: true },
 *     { id: 'status', label: 'Status', sortable: true, filterable: true, filterType: 'select' },
 *     { id: 'amount', label: 'Amount', sortable: true, filterable: true, filterType: 'number' },
 *   ];
 *   grid.rows = [
 *     { id: '1', name: 'Acme Corp', status: 'Active',  amount: 1200 },
 *     { id: '2', name: 'Globex',    status: 'Trial',   amount:  450 },
 *   ];
 * </script>
 */

import { dispatchEvent } from '../../../utils/events.js';

const template = document.createElement('template');
template.innerHTML = `
<style>
  :host {
    display: block;
    font-family: var(--ctl-font-sans, system-ui, -apple-system, Segoe UI, Roboto, Arial);
    color: var(--ctl-color-text);
  }

  .frame {
    border: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
    border-radius: var(--ctl-radius-lg);
    box-shadow: var(--ctl-shadow-card, 0 1px 4px rgba(0,0,0,.06));
    overflow: hidden;
  }

  /* toolbar */
  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--ctl-space-3);
    border-bottom: 1px solid var(--ctl-color-border);
    flex-wrap: wrap;
    gap: var(--ctl-space-2);
  }
  .toolbar:empty { display: none; }

  .tb-left { display: flex; align-items: center; gap: var(--ctl-space-2); }
  .tb-right { display: flex; align-items: center; gap: var(--ctl-space-2); }

  .search-input {
    all: unset;
    font-family: inherit;
    font-size: var(--ctl-font-size-sm);
    color: var(--ctl-color-text);
    border: 1px solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-md);
    padding: 6px 10px;
    width: 220px;
    background: var(--ctl-color-surface);
    box-sizing: border-box;
    transition: border-color var(--ctl-transition-fast), box-shadow var(--ctl-transition-fast);
  }
  .search-input::placeholder { color: var(--ctl-color-muted); }
  .search-input:focus {
    border-color: var(--ctl-color-brand);
    box-shadow: var(--ctl-focus-ring);
    outline: none;
  }

  .btn {
    all: unset;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: var(--ctl-space-1);
    padding: 6px 12px;
    border-radius: var(--ctl-radius-md);
    font-size: var(--ctl-font-size-sm);
    font-weight: var(--ctl-font-weight-medium, 500);
    border: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
    color: var(--ctl-color-text);
    transition: background var(--ctl-transition-fast);
    white-space: nowrap;
  }
  .btn:hover { background: color-mix(in srgb, var(--ctl-color-border) 50%, var(--ctl-color-surface)); }
  .btn:focus-visible { outline: none; box-shadow: var(--ctl-focus-ring); }
  .btn-primary {
    background: var(--ctl-color-brand);
    color: var(--ctl-color-on-brand, #fff);
    border-color: var(--ctl-color-brand);
  }
  .btn-primary:hover { background: color-mix(in srgb, var(--ctl-color-brand) 85%, #000); }

  /* table wrapper – scroll container */
  .table-wrap {
    overflow: auto;
    position: relative;
  }

  table {
    border-collapse: separate;
    border-spacing: 0;
    width: 100%;
    min-width: 400px;
    table-layout: fixed;
  }

  /* sticky header */
  thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: var(--ctl-color-surface);
    border-bottom: 2px solid var(--ctl-color-border);
    text-align: left;
    font-size: var(--ctl-font-size-sm);
    font-weight: var(--ctl-font-weight-bold);
    color: var(--ctl-color-muted);
    white-space: nowrap;
    user-select: none;
  }

  th, td {
    padding: var(--ctl-space-3);
    border-bottom: 1px solid var(--ctl-color-border);
    vertical-align: middle;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  :host([density="compact"]) th,
  :host([density="compact"]) td { padding: var(--ctl-space-2) var(--ctl-space-3); }

  .th-inner {
    display: flex;
    align-items: center;
    gap: var(--ctl-space-1);
    position: relative;
  }

  .sort-btn {
    all: unset;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex: 1;
    min-width: 0;
    padding: 2px 4px;
    border-radius: var(--ctl-radius-sm);
  }
  .sort-btn:hover { background: color-mix(in srgb, var(--ctl-color-border) 50%, transparent); }
  .sort-btn:focus-visible { outline: none; box-shadow: var(--ctl-focus-ring); }

  .sort-icon { color: var(--ctl-color-muted); flex-shrink: 0; }

  /* resize handle */
  .resize-handle {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 6px;
    cursor: col-resize;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  :host(:not([resizable])) .resize-handle { display: none; }
  .resize-handle::after {
    content: '';
    display: block;
    width: 2px;
    height: 60%;
    background: var(--ctl-color-border);
    border-radius: 1px;
    transition: background var(--ctl-transition-fast);
  }
  .resize-handle:hover::after,
  .resize-handle.dragging::after { background: var(--ctl-color-brand); }

  /* filter row */
  .filter-row th {
    background: color-mix(in srgb, var(--ctl-color-surface-2, var(--ctl-color-surface)) 80%, var(--ctl-color-surface));
    padding: var(--ctl-space-2) var(--ctl-space-3);
    border-bottom: 1px solid var(--ctl-color-border);
    position: sticky;
    top: var(--ctl-grid-header-height, 45px);
    z-index: 2;
  }
  :host(:not([filterable])) .filter-row { display: none; }

  .filter-input {
    all: unset;
    font-family: inherit;
    font-size: var(--ctl-font-size-xs);
    color: var(--ctl-color-text);
    border: 1px solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-sm);
    padding: 4px 7px;
    width: 100%;
    background: var(--ctl-color-surface);
    box-sizing: border-box;
    transition: border-color var(--ctl-transition-fast);
  }
  .filter-input::placeholder { color: var(--ctl-color-muted); }
  .filter-input:focus {
    outline: none;
    border-color: var(--ctl-color-brand);
    box-shadow: var(--ctl-focus-ring);
  }

  /* body rows */
  tbody tr { background: var(--ctl-color-surface); transition: background var(--ctl-transition-fast); }
  tbody tr:hover { background: color-mix(in srgb, var(--ctl-color-brand) 5%, var(--ctl-color-surface)); }
  tbody tr[data-selected="true"] { background: color-mix(in srgb, var(--ctl-color-brand) 10%, var(--ctl-color-surface)); }

  /* expand toggle */
  .expand-btn {
    all: unset;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: var(--ctl-radius-sm);
    color: var(--ctl-color-muted);
    transition: transform var(--ctl-transition-fast);
    flex-shrink: 0;
  }
  .expand-btn:hover { color: var(--ctl-color-brand); }
  .expand-btn:focus-visible { outline: none; box-shadow: var(--ctl-focus-ring); }
  .expand-btn[aria-expanded="true"] { transform: rotate(90deg); }

  .expand-row td {
    padding: 0;
    border-bottom: 1px solid var(--ctl-color-border);
    background: color-mix(in srgb, var(--ctl-color-surface-2, var(--ctl-color-surface)) 80%, var(--ctl-color-surface));
  }
  .expand-row[hidden] { display: none; }

  .expand-content {
    padding: var(--ctl-space-3);
    font-size: var(--ctl-font-size-sm);
    color: var(--ctl-color-muted);
  }

  /* selection checkbox column */
  .sel-col { width: 44px; text-align: center; }
  .chk {
    width: 16px;
    height: 16px;
    accent-color: var(--ctl-color-brand);
    cursor: pointer;
  }
  :host(:not([selectable])) .sel-col { display: none; }

  /* expand column */
  .exp-col { width: 44px; text-align: center; }
  :host(:not([expandable])) .exp-col { display: none; }

  /* drag-over column indicator */
  th[data-drag-over="true"] { background: color-mix(in srgb, var(--ctl-color-brand) 12%, var(--ctl-color-surface)); }

  /* empty */
  .empty-row td {
    padding: var(--ctl-space-5, 40px);
    text-align: center;
    color: var(--ctl-color-muted);
    font-size: var(--ctl-font-size-sm);
  }

  /* pagination */
  .pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--ctl-space-3);
    border-top: 1px solid var(--ctl-color-border);
    flex-wrap: wrap;
    gap: var(--ctl-space-2);
    font-size: var(--ctl-font-size-sm);
    color: var(--ctl-color-muted);
  }
  .page-btns { display: flex; gap: var(--ctl-space-1); }
  .page-btn {
    all: unset;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: var(--ctl-radius-md);
    font-size: var(--ctl-font-size-sm);
    border: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
    color: var(--ctl-color-text);
    transition: background var(--ctl-transition-fast);
  }
  .page-btn:hover:not(:disabled) {
    background: color-mix(in srgb, var(--ctl-color-brand) 8%, var(--ctl-color-surface));
    border-color: var(--ctl-color-brand);
    color: var(--ctl-color-brand);
  }
  .page-btn:focus-visible { outline: none; box-shadow: var(--ctl-focus-ring); }
  .page-btn[aria-current="page"] {
    background: var(--ctl-color-brand);
    color: var(--ctl-color-on-brand, #fff);
    border-color: var(--ctl-color-brand);
  }
  .page-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  .page-size-select {
    all: unset;
    font-family: inherit;
    font-size: var(--ctl-font-size-sm);
    border: 1px solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-sm);
    padding: 4px 8px;
    background: var(--ctl-color-surface);
    color: var(--ctl-color-text);
    cursor: pointer;
  }
  .page-size-select:focus { outline: 2px solid var(--ctl-color-brand); }

  .sr-only {
    position: absolute;
    width: 1px; height: 1px;
    padding: 0; margin: -1px;
    overflow: hidden; clip: rect(0,0,0,0);
    white-space: nowrap; border: 0;
  }
</style>

<div class="frame" part="frame">
  <div class="toolbar" part="toolbar">
    <div class="tb-left">
      <input class="search-input" type="search" placeholder="Search all columns…" aria-label="Search table">
    </div>
    <div class="tb-right">
      <button class="btn export-btn" type="button" aria-label="Export CSV" hidden>
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M8 1v10M4 7l4 4 4-4M2 12v1a2 2 0 002 2h8a2 2 0 002-2v-1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        Export CSV
      </button>
    </div>
  </div>

  <div class="table-wrap" part="table-wrap">
    <table role="grid" aria-label="Data grid">
      <thead>
        <tr class="header-row"></tr>
        <tr class="filter-row"></tr>
      </thead>
      <tbody></tbody>
    </table>
  </div>

  <div class="pagination" part="pagination" hidden>
    <div class="page-info"></div>
    <div class="page-btns"></div>
    <div class="page-size-wrap">
      Rows per page:
      <select class="page-size-select" aria-label="Rows per page">
        <option value="10">10</option>
        <option value="25" selected>25</option>
        <option value="50">50</option>
        <option value="100">100</option>
      </select>
    </div>
  </div>
</div>

<div class="sr-only" aria-live="polite" aria-atomic="true"></div>
`;

export class CtlDataGrid extends HTMLElement {
  static get observedAttributes() {
    return ['filterable', 'resizable', 'exportable', 'selectable', 'expandable',
            'page-size', 'server-side', 'density'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' }).appendChild(template.content.cloneNode(true));

    this._columns = [];
    this._allRows = [];      // all source rows
    this._filteredRows = []; // after global search + column filters
    this._displayRows = [];  // current page slice
    this._columnFilters = {};
    this._globalQuery = '';
    this._sort = { col: null, dir: 'asc' };
    this._selectedIds = new Set();
    this._expandedIds = new Set();
    this._currentPage = 1;
    this._pageSize = 25;
    this._totalRows = null; // server-side total

    // drag state
    this._resizeDrag = null;
    this._reorderDrag = null;

    this._thead = this.shadowRoot.querySelector('.header-row');
    this._filterRow = this.shadowRoot.querySelector('.filter-row');
    this._tbody = this.shadowRoot.querySelector('tbody');
    this._pager = this.shadowRoot.querySelector('.pagination');
    this._pageInfo = this.shadowRoot.querySelector('.page-info');
    this._pageBtns = this.shadowRoot.querySelector('.page-btns');
    this._pageSizeSelect = this.shadowRoot.querySelector('.page-size-select');
    this._exportBtn = this.shadowRoot.querySelector('.export-btn');
    this._searchInput = this.shadowRoot.querySelector('.search-input');
    this._live = this.shadowRoot.querySelector('[aria-live]');

    // Bound references for proper add/remove listener pairing
    this._boundResizeMouseMove = this._onResizeMouseMove.bind(this);
    this._boundResizeMouseUp = this._onResizeMouseUp.bind(this);
    this._boundReorderMouseMove = this._onReorderMouseMove.bind(this);
    this._boundReorderMouseUp = this._onReorderMouseUp.bind(this);
  }

  connectedCallback() {
    this._searchInput.addEventListener('input', e => {
      this._globalQuery = e.target.value.trim().toLowerCase();
      this._currentPage = 1;
      this._applyFiltersAndRender();
    });

    this._exportBtn.addEventListener('click', () => this._exportCsv());

    this._pageSizeSelect.addEventListener('change', e => {
      this._pageSize = parseInt(e.target.value, 10);
      this._currentPage = 1;
      this._applyFiltersAndRender();
    });

    document.addEventListener('mousemove', this._boundResizeMouseMove);
    document.addEventListener('mouseup', this._boundResizeMouseUp);
    document.addEventListener('mousemove', this._boundReorderMouseMove);
    document.addEventListener('mouseup', this._boundReorderMouseUp);
  }

  disconnectedCallback() {
    document.removeEventListener('mousemove', this._boundResizeMouseMove);
    document.removeEventListener('mouseup', this._boundResizeMouseUp);
    document.removeEventListener('mousemove', this._boundReorderMouseMove);
    document.removeEventListener('mouseup', this._boundReorderMouseUp);
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (name === 'page-size' && newVal) {
      this._pageSize = parseInt(newVal, 10) || 25;
    }
    if (name === 'exportable') {
      this._exportBtn.hidden = !this.hasAttribute('exportable');
    }
    this._renderTable();
  }

  // ─── Public API ───────────────────────────────────────────────────────────

  get columns() { return this._columns; }
  set columns(val) {
    this._columns = Array.isArray(val) ? val : [];
    this._columnFilters = {};
    this._renderHeader();
    this._renderFilterRow();
    this._applyFiltersAndRender();
  }

  get rows() { return this._allRows; }
  set rows(val) {
    this._allRows = Array.isArray(val) ? val : [];
    this._currentPage = 1;
    this._applyFiltersAndRender();
  }

  /** For server-side pagination: set pre-paginated rows + total count */
  setServerPage(rows, totalCount) {
    this._displayRows = Array.isArray(rows) ? rows : [];
    this._totalRows = totalCount;
    this._renderRows();
    this._renderPagination();
  }

  get selectedIds() { return [...this._selectedIds]; }

  get sort() { return { ...this._sort }; }

  // ─── Rendering ────────────────────────────────────────────────────────────

  _renderHeader() {
    this._thead.innerHTML = '';

    if (this.hasAttribute('expandable')) {
      const th = document.createElement('th');
      th.className = 'exp-col';
      this._thead.appendChild(th);
    }

    if (this.hasAttribute('selectable')) {
      const th = document.createElement('th');
      th.className = 'sel-col';
      const chk = document.createElement('input');
      chk.type = 'checkbox';
      chk.className = 'chk';
      chk.setAttribute('aria-label', 'Select all');
      chk.addEventListener('change', () => {
        if (chk.checked) this._displayRows.forEach((r, idx) => this._selectedIds.add(String(r.id ?? idx)));
        else this._selectedIds.clear();
        this._renderRows();
        dispatchEvent(this, 'ctl-selection-change', { ids: this.selectedIds });
      });
      th.appendChild(chk);
      this._thead.appendChild(th);
    }

    this._columns.forEach((col, colIdx) => {
      const th = document.createElement('th');
      th.dataset.colId = col.id;
      th.dataset.colIdx = String(colIdx);
      if (col.width) th.style.width = col.width;
      th.setAttribute('draggable', this.hasAttribute('reorderable') ? 'true' : 'false');
      th.setAttribute('aria-sort', 'none');

      const inner = document.createElement('div');
      inner.className = 'th-inner';

      if (col.sortable) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sort-btn';
        btn.innerHTML = `<span>${_escapeHtml(col.label)}</span>${_sortIcon('none')}`;
        btn.setAttribute('aria-label', `Sort by ${col.label}`);
        btn.addEventListener('click', () => this._toggleSort(col.id));
        inner.appendChild(btn);
      } else {
        const span = document.createElement('span');
        span.textContent = col.label;
        inner.appendChild(span);
      }

      if (this.hasAttribute('resizable')) {
        const handle = document.createElement('div');
        handle.className = 'resize-handle';
        handle.setAttribute('aria-hidden', 'true');
        handle.addEventListener('mousedown', e => this._onResizeMouseDown(e, th, col.id));
        inner.appendChild(handle);
      }

      th.appendChild(inner);

      if (this.hasAttribute('reorderable')) {
        th.addEventListener('dragstart', e => this._onReorderDragStart(e, colIdx));
        th.addEventListener('dragover', e => this._onReorderDragOver(e, colIdx));
        th.addEventListener('dragleave', () => th.removeAttribute('data-drag-over'));
        th.addEventListener('drop', e => this._onReorderDrop(e, colIdx));
        th.addEventListener('dragend', () => {
          this._thead.querySelectorAll('th').forEach(t => t.removeAttribute('data-drag-over'));
        });
      }

      this._thead.appendChild(th);
    });
  }

  _renderFilterRow() {
    this._filterRow.innerHTML = '';
    if (!this.hasAttribute('filterable')) return;

    if (this.hasAttribute('expandable')) this._filterRow.appendChild(document.createElement('th'));
    if (this.hasAttribute('selectable')) this._filterRow.appendChild(document.createElement('th'));

    this._columns.forEach(col => {
      const th = document.createElement('th');
      if (!col.filterable) { this._filterRow.appendChild(th); return; }

      const input = document.createElement('input');
      input.className = 'filter-input';
      input.placeholder = `Filter…`;
      input.type = col.filterType === 'number' ? 'number' : col.filterType === 'date' ? 'date' : 'text';
      input.setAttribute('aria-label', `Filter ${col.label}`);
      input.value = this._columnFilters[col.id] || '';
      input.addEventListener('input', e => {
        this._columnFilters[col.id] = e.target.value.trim().toLowerCase();
        this._currentPage = 1;
        this._applyFiltersAndRender();
      });
      th.appendChild(input);
      this._filterRow.appendChild(th);
    });
  }

  _applyFiltersAndRender() {
    if (this.hasAttribute('server-side')) {
      // Just re-emit the request; the consumer calls setServerPage()
      dispatchEvent(this, 'ctl-load', {
        page: this._currentPage,
        pageSize: this._pageSize,
        sort: this._sort,
        globalQuery: this._globalQuery,
        columnFilters: { ...this._columnFilters },
      });
      return;
    }

    let rows = this._allRows.slice();

    // global search
    if (this._globalQuery) {
      rows = rows.filter(row =>
        this._columns.some(col => {
          const v = String(row[col.id] ?? '').toLowerCase();
          return v.includes(this._globalQuery);
        })
      );
    }

    // column filters
    Object.entries(this._columnFilters).forEach(([colId, q]) => {
      if (!q) return;
      rows = rows.filter(row => String(row[colId] ?? '').toLowerCase().includes(q));
    });

    // sort
    if (this._sort.col) {
      const { col, dir } = this._sort;
      rows = rows.slice().sort((a, b) => {
        const av = a[col] ?? '';
        const bv = b[col] ?? '';
        const n = typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv));
        return dir === 'asc' ? n : -n;
      });
    }

    this._filteredRows = rows;

    // paginate
    const total = rows.length;
    const start = (this._currentPage - 1) * this._pageSize;
    this._displayRows = rows.slice(start, start + this._pageSize);

    this._renderRows();
    this._renderPagination();
    this._updateSortIcons();
    this._live.textContent = `${total} row${total !== 1 ? 's' : ''} found.`;
  }

  _renderTable() {
    this._renderHeader();
    this._renderFilterRow();
    this._applyFiltersAndRender();
  }

  _renderRows() {
    this._tbody.innerHTML = '';

    if (!this._displayRows.length) {
      const tr = document.createElement('tr');
      tr.className = 'empty-row';
      const td = document.createElement('td');
      const span = this._columns.length + (this.hasAttribute('selectable') ? 1 : 0) + (this.hasAttribute('expandable') ? 1 : 0);
      td.colSpan = span || 1;
      td.textContent = 'No data';
      tr.appendChild(td);
      this._tbody.appendChild(tr);
      return;
    }

    this._displayRows.forEach((row, rowIdx) => {
      const rowId = String(row.id ?? rowIdx);
      const tr = document.createElement('tr');
      tr.dataset.rowId = rowId;
      tr.dataset.selected = String(this._selectedIds.has(rowId));

      // expand col
      if (this.hasAttribute('expandable')) {
        const td = document.createElement('td');
        td.className = 'exp-col';
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'expand-btn';
        btn.setAttribute('aria-expanded', String(this._expandedIds.has(rowId)));
        btn.setAttribute('aria-label', 'Expand row');
        btn.innerHTML = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M4 2l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
        btn.addEventListener('click', () => this._toggleExpand(rowId));
        td.appendChild(btn);
        tr.appendChild(td);
      }

      // select col
      if (this.hasAttribute('selectable')) {
        const td = document.createElement('td');
        td.className = 'sel-col';
        const chk = document.createElement('input');
        chk.type = 'checkbox';
        chk.className = 'chk';
        chk.checked = this._selectedIds.has(rowId);
        chk.setAttribute('aria-label', `Select row ${rowId}`);
        chk.addEventListener('change', () => {
          if (chk.checked) this._selectedIds.add(rowId);
          else this._selectedIds.delete(rowId);
          tr.dataset.selected = String(chk.checked);
          dispatchEvent(this, 'ctl-selection-change', { ids: this.selectedIds });
        });
        td.appendChild(chk);
        tr.appendChild(td);
      }

      this._columns.forEach(col => {
        const td = document.createElement('td');
        const v = row[col.id];
        if (col.render) {
          td.innerHTML = col.render(v, row);
        } else {
          td.textContent = v ?? '';
        }
        if (col.align) td.style.textAlign = col.align;
        tr.appendChild(td);
      });

      this._tbody.appendChild(tr);

      // expansion row
      if (this.hasAttribute('expandable')) {
        const expTr = document.createElement('tr');
        expTr.className = 'expand-row';
        if (!this._expandedIds.has(rowId)) expTr.hidden = true;
        const expTd = document.createElement('td');
        const span = this._columns.length + (this.hasAttribute('selectable') ? 1 : 0) + 1;
        expTd.colSpan = span;
        const content = document.createElement('div');
        content.className = 'expand-content';
        content.innerHTML = this._renderExpandContent(row);
        expTd.appendChild(content);
        expTr.appendChild(expTd);
        this._tbody.appendChild(expTr);
      }
    });
  }

  _renderExpandContent(row) {
    return Object.entries(row)
      .filter(([k]) => k !== 'id')
      .map(([k, v]) => `<strong>${_escapeHtml(k)}:</strong> ${_escapeHtml(String(v ?? ''))}`)
      .join(' &nbsp;|&nbsp; ');
  }

  _renderPagination() {
    const serverSide = this.hasAttribute('server-side');
    const total = serverSide ? (this._totalRows ?? 0) : this._filteredRows.length;

    if (total <= this._pageSize && this._currentPage === 1 && !serverSide) {
      this._pager.hidden = true;
      return;
    }
    this._pager.hidden = false;

    const pageCount = Math.ceil(total / this._pageSize);
    const start = (this._currentPage - 1) * this._pageSize + 1;
    const end = Math.min(this._currentPage * this._pageSize, total);

    this._pageInfo.textContent = total > 0 ? `${start}–${end} of ${total}` : '0 results';
    this._pageSizeSelect.value = String(this._pageSize);

    this._pageBtns.innerHTML = '';

    // Prev
    const prev = _makePageBtn('‹', false, this._currentPage <= 1, 'Previous page');
    prev.addEventListener('click', () => { this._currentPage--; this._applyFiltersAndRender(); });
    this._pageBtns.appendChild(prev);

    // Page numbers (show at most 5 around current)
    const range = _pageRange(this._currentPage, pageCount);
    range.forEach(p => {
      if (p === '…') {
        const el = document.createElement('span');
        el.textContent = '…';
        el.style.padding = '0 4px';
        el.style.display = 'inline-flex';
        el.style.alignItems = 'center';
        this._pageBtns.appendChild(el);
        return;
      }
      const btn = _makePageBtn(String(p), p === this._currentPage, false, `Page ${p}`);
      if (p === this._currentPage) btn.setAttribute('aria-current', 'page');
      btn.addEventListener('click', () => { this._currentPage = p; this._applyFiltersAndRender(); });
      this._pageBtns.appendChild(btn);
    });

    // Next
    const next = _makePageBtn('›', false, this._currentPage >= pageCount, 'Next page');
    next.addEventListener('click', () => { this._currentPage++; this._applyFiltersAndRender(); });
    this._pageBtns.appendChild(next);
  }

  // ─── Sort ─────────────────────────────────────────────────────────────────

  _toggleSort(colId) {
    if (this._sort.col === colId) {
      this._sort.dir = this._sort.dir === 'asc' ? 'desc' : 'asc';
    } else {
      this._sort = { col: colId, dir: 'asc' };
    }
    this._currentPage = 1;
    this._applyFiltersAndRender();
    dispatchEvent(this, 'ctl-sort', { sort: { ...this._sort } });
  }

  _updateSortIcons() {
    this._thead.querySelectorAll('th[data-col-id]').forEach(th => {
      const colId = th.dataset.colId;
      const btn = th.querySelector('.sort-btn');
      if (!btn) return;

      const dir = this._sort.col === colId ? this._sort.dir : 'none';
      th.setAttribute('aria-sort', dir === 'none' ? 'none' : dir === 'asc' ? 'ascending' : 'descending');
      const icon = btn.querySelector('.sort-icon');
      if (icon) icon.outerHTML = _sortIcon(dir);
    });
  }

  // ─── Expand rows ──────────────────────────────────────────────────────────

  _toggleExpand(rowId) {
    if (this._expandedIds.has(rowId)) this._expandedIds.delete(rowId);
    else this._expandedIds.add(rowId);
    this._renderRows();
    dispatchEvent(this, 'ctl-row-expand', { id: rowId, expanded: this._expandedIds.has(rowId) });
  }

  // ─── Column resize ────────────────────────────────────────────────────────

  _onResizeMouseDown(e, th, colId) {
    e.preventDefault();
    e.stopPropagation();
    this._resizeDrag = {
      th,
      colId,
      startX: e.clientX,
      startW: th.offsetWidth,
    };
    e.target.classList.add('dragging');
  }

  _onResizeMouseMove(e) {
    if (!this._resizeDrag) return;
    const { th, startX, startW } = this._resizeDrag;
    const delta = e.clientX - startX;
    const newW = Math.max(60, startW + delta);
    th.style.width = `${newW}px`;
  }

  _onResizeMouseUp(e) {
    if (!this._resizeDrag) return;
    const { th, colId } = this._resizeDrag;
    th.querySelector('.resize-handle')?.classList.remove('dragging');
    dispatchEvent(this, 'ctl-column-resize', { colId, width: th.offsetWidth });
    this._resizeDrag = null;
  }

  // ─── Column reorder (drag-and-drop) ───────────────────────────────────────

  _onReorderDragStart(e, colIdx) {
    this._reorderDrag = { fromIdx: colIdx };
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(colIdx));
  }

  _onReorderDragOver(e, colIdx) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const th = this._thead.querySelectorAll('th[data-col-idx]')[colIdx];
    if (th) th.dataset.dragOver = 'true';
  }

  _onReorderMouseMove() {} // handled via drag events
  _onReorderMouseUp() {}

  _onReorderDrop(e, targetIdx) {
    e.preventDefault();
    if (!this._reorderDrag) return;
    const { fromIdx } = this._reorderDrag;
    if (fromIdx === targetIdx) return;

    const cols = this._columns.slice();
    const [moved] = cols.splice(fromIdx, 1);
    cols.splice(targetIdx, 0, moved);
    this._columns = cols;

    this._renderHeader();
    this._renderFilterRow();
    this._renderRows();

    dispatchEvent(this, 'ctl-column-reorder', { columns: this._columns.map(c => c.id) });
    this._reorderDrag = null;
  }

  // ─── CSV export ───────────────────────────────────────────────────────────

  _exportCsv() {
    const rows = this.hasAttribute('server-side') ? this._displayRows : this._filteredRows;
    const headers = this._columns.map(c => _csvCell(c.label));
    const lines = [headers.join(',')];

    rows.forEach(row => {
      lines.push(this._columns.map(col => _csvCell(String(row[col.id] ?? ''))).join(','));
    });

    const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'export.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    dispatchEvent(this, 'ctl-export', { format: 'csv', rowCount: rows.length });
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function _sortIcon(dir) {
  if (dir === 'asc') return `<svg class="sort-icon" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M6 9V3M3 6l3-3 3 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  if (dir === 'desc') return `<svg class="sort-icon" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M6 3v6M3 6l3 3 3-3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  return `<svg class="sort-icon" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true" style="opacity:.4"><path d="M6 2v8M3 5l3-3 3 3M3 7l3 3 3-3" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
}

function _makePageBtn(label, current, disabled, ariaLabel) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'page-btn';
  btn.textContent = label;
  btn.disabled = disabled;
  btn.setAttribute('aria-label', ariaLabel);
  if (current) btn.setAttribute('aria-current', 'page');
  return btn;
}

function _pageRange(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [];
  if (current <= 4) {
    pages.push(1, 2, 3, 4, 5, '…', total);
  } else if (current >= total - 3) {
    pages.push(1, '…', total - 4, total - 3, total - 2, total - 1, total);
  } else {
    pages.push(1, '…', current - 1, current, current + 1, '…', total);
  }
  return pages;
}

function _escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function _csvCell(val) {
  const s = String(val ?? '');
  if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

if (!customElements.get('ctl-data-grid')) {
  customElements.define('ctl-data-grid', CtlDataGrid);
}
